import update from 'immutability-helper';
import uuid from 'uuid-random';
import * as Types from './types';
import { createReducer } from '../../utils';

const MultimediaCollection = Object.freeze({
  IMAGE: 'pictures',
  VIDEO: 'videos',
  VIDEO360: 'tour360',
  PLAN: 'plan'
});

const MultimediaType = Object.freeze({
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  TOUR360: 'TOUR360',
  PLAN: 'PLAN'
});

function normalize(data) {
  const result = {
    pictures: [],
    videos: [],
    plan: [],
    tour360: []
  };
  Object.values(MultimediaCollection).forEach(k => {
    result[k] = data[k]
      ? data[k]
          .map(multimediaItem => ({ ...multimediaItem, id: uuid(), error: null }))
          .sort((a, b) => a.order - b.order)
      : [];
  });
  return result;
}

function deleteMultimedia(id, collection) {
  const index = collection.findIndex(m => m.id === id);
  const multimedia = collection[index];
  const replace = multimedia.id_multimedia ? [index, 1, { ...multimedia, delete: true }] : [index, 1];
  return update(collection, {
    $splice: [replace]
  }).map((item, i) => {
    if (i > index) {
      const toUpdate = i + 1 === item.order ? { order: item.order } : { order: i + 1, updated: true };
      return { ...item, ...toUpdate };
    }
    return item;
  });
}

function addErrorMultimedia(id, collection, keyError = 'publisher.step2.module1.picture.error.generic') {
  const index = collection.findIndex(m => m.id === id);
  const multimedia = collection[index];

  return update(collection, {
    $splice: [[index, 1, { ...multimedia, error: multimedia.error || keyError }]]
  });
}

function cleanMultimedia(collection) {
  const multimedia = collection.filter(p => !p.error);

  return update(collection, {
    $set: multimedia
  });
}

function updateMultimedia(updatedMultimedia, collection) {
  const index = collection.findIndex(m => m.id === updatedMultimedia.id);
  const multimedia = collection[index];
  return update(collection, {
    $splice: [[index, 1, { ...multimedia, ...updatedMultimedia, updated: true }]]
  });
}

function moveMultimedia(dragIndex, hoverIndex, collection) {
  const draggedMultimedia = collection[dragIndex];
  const tmp = update(collection, { $splice: [[dragIndex, 1], [hoverIndex, 0, draggedMultimedia]] });
  return tmp.map((item, index) => {
    const toUpdate = index + 1 === item.order ? { order: item.order } : { order: index + 1, updated: true };
    return { ...item, ...toUpdate };
  });
}

function addMultimedia(multimedia, collection) {
  const order = collection.length === 0 || multimedia.multimediaType === 'VIDEO' ? 1 : collection.length + 1;

  const formatted = {
    ...multimedia,
    id: multimedia.id || uuid(),
    delete: false,
    tag360: false,
    title: null,
    id_multimedia: null,
    order,
    error: multimedia.error || null
  };

  if (multimedia.multimediaType === 'VIDEO') {
    collection.map(item => {
      item.order += 1;
      item.updated = true;
      return item;
    });

    return update(collection, {
      $unshift: [formatted]
    });
  }

  return update(collection, {
    $push: [formatted]
  });
}

const initialMultimediaState = {
  multimedias: {
    videos: [],
    pictures: [],
    tour360: [],
    plan: []
  },
  originalMultimedia: null,
  processing: {},
  error: null,
  loading: false
};

const multimediaReducer = createReducer(initialMultimediaState)({
  [Types.GET_STEP_MULTIMEDIA]: state => ({ ...state, error: null, loading: true }),
  [`${Types.GET_STEP_MULTIMEDIA}_COMPLETED`]: (state, action) => ({
    ...state,
    loading: false,
    multimedias: normalize(action.payload),
    originalMultimedia: normalize(action.payload)
  }),
  [`${Types.GET_STEP_MULTIMEDIA}_FAILED`]: (state, action) => ({
    ...state,
    loading: false,
    error: action.payload
  }),
  [Types.ADD_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: addMultimedia(action.payload.multimedia, state.multimedias[action.payload.type])
    }
  }),
  [Types.MOVE_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: moveMultimedia(
        action.payload.dragIndex,
        action.payload.hoverIndex,
        state.multimedias[action.payload.type]
      )
    }
  }),
  [Types.UPDATE_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: updateMultimedia(action.payload.multimedia, state.multimedias[action.payload.type])
    }
  }),
  [Types.DELETE_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: deleteMultimedia(action.payload.id, state.multimedias[action.payload.type])
    }
  }),
  [Types.ADD_ERROR_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: addErrorMultimedia(
        action.payload.id,
        state.multimedias[action.payload.type],
        action.payload.keyError
      )
    }
  }),
  [Types.CLEAN_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: cleanMultimedia(state.multimedias[action.payload.type])
    }
  }),
  [Types.PROCESSING_MULTIMEDIA]: (state, action) => ({
    ...state,
    processing: { ...state.processing, [action.payload.key]: action.payload.val }
  })
});

export { multimediaReducer, MultimediaType, MultimediaCollection };
